import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import React, { useEffect } from 'react';

import { GetMuseumDocument } from '../../graphql/autogen/hooks';
import { GetMuseumQuery } from '../../graphql/autogen/operations';
import { defaultPageProps } from '../../libs/ssr/getDefaultPageProps';
import {
  createNextSeoProps,
  getExhibitionSeoProps,
  SeoProps,
} from '../../libs/ssr/getSeoPageProps';
import { localeTypes } from '../../locales';

type OgpPageProps = {
  seoProps: SeoProps | null;
  lpUrl?: string | null;
};

const OgpPage: NextPage<OgpPageProps> = ({ seoProps, lpUrl }) => {
  const { pageUrl, title, thumbnailMedia, artworkImage } = seoProps ?? {};

  const nextSeoProps = createNextSeoProps(
    pageUrl as string,
    title,
    thumbnailMedia,
    artworkImage,
  );

  const lpExhibitionPath = '/exhibition/pokemon-card';
  useEffect(() => {
    if (!lpUrl) {
      return;
    }

    const url = new URL(lpUrl);
    const { origin } = url;
    const browserLang = window.navigator.language;
    // NOTE: 繁体字はLPではzh_Hantとできない為、LP用はzh-hantとする。
    const zhHantLp = 'zh-hant';
    // ブラウザの言語情報を見て、LPの飛び先を変える
    let redirectTo = undefined;
    switch (browserLang) {
      case localeTypes.Japanese: {
        redirectTo = `${origin}${lpExhibitionPath}`;
        break;
      }
      case localeTypes.Spanish: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.Spanish}`;
        break;
      }
      case localeTypes.French: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.French}`;
        break;
      }
      case localeTypes.German: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.German}`;
        break;
      }
      case localeTypes.Italian: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.Italian}`;
        break;
      }
      // MEMO: browser上だとzh-Hantが存在しないため、zhがつくものはすべてzh-Hantとする
      case 'zh': {
        redirectTo = `${origin}${lpExhibitionPath}/${zhHantLp}`;
        break;
      }
      case 'zh-TW': {
        redirectTo = `${origin}${lpExhibitionPath}/${zhHantLp}`;
        break;
      }
      case 'zh-HK': {
        redirectTo = `${origin}${lpExhibitionPath}/${zhHantLp}`;
        break;
      }
      case 'zh-CN': {
        redirectTo = `${origin}${lpExhibitionPath}/${zhHantLp}`;
        break;
      }
      case localeTypes.Korean: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.Korean}`;
        break;
      }
      case localeTypes.Thai: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.Thai}`;
        break;
      }
      case localeTypes.Indonesian: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.Indonesian}`;
        break;
      }
      default: {
        redirectTo = `${origin}${lpExhibitionPath}/${localeTypes.English}`;
        break;
      }
    }

    location.href = redirectTo;
  }, [lpUrl]);

  return <NextSeo {...nextSeoProps} />;
};

const getServerSideProps: GetServerSideProps<OgpPageProps> = async (
  ctx: GetServerSidePropsContext,
) => {
  // NOTE: pokemon-card限定のため、期間終了後削除
  const fallbackLpUrl = 'https://lp.anique.jp/exhibition/pokemon-card';
  const slug = ctx.query.slug;
  const { locale } = ctx;
  const { apollo } = await defaultPageProps(ctx, true);

  try {
    const getMuseumResult = await apollo.query({
      query: GetMuseumDocument,
      variables: {
        museumSlug: slug,
        locale,
      },
    });
    const museumMetaData = getMuseumResult.data as GetMuseumQuery;
    const { lpUrl = fallbackLpUrl } = museumMetaData.museum;

    const seoProps = await getExhibitionSeoProps(apollo, ctx);

    return {
      props: {
        seoProps,
        lpUrl,
      },
    };
  } catch (e) {
    return {
      props: {
        seoProps: null,
        lpUrl: null,
      },
    };
  }
};

export default OgpPage;
export { getServerSideProps };
